import type { Location, Relationship } from '@/lib/types';

type LocationReferenceFields = Pick<
  Location,
  'mapEntityAreas' | 'mapEntityPoints' | 'parentId' | 'parentName'
>;

type LocationMapReferenceUpdates = Pick<
  LocationReferenceFields,
  'mapEntityAreas' | 'mapEntityPoints'
>;

export interface LocationReferenceCleanup {
  clearParent: boolean;
  updates: Partial<LocationMapReferenceUpdates>;
}

/** Whether a relationship has the location as one of its typed endpoints. */
export function relationshipReferencesLocation(
  relationship: Relationship,
  locationId: string
): boolean {
  return (
    relationship.entityAType === 'locations'
    && relationship.entityAId === locationId
  ) || (
    relationship.entityBType === 'locations'
    && relationship.entityBId === locationId
  );
}

/**
 * Builds the minimal update needed to remove references to a deleted location
 * from another location. Returns null when no update is needed.
 */
export function getLocationReferenceCleanup(
  location: LocationReferenceFields,
  deletedLocationId: string
): LocationReferenceCleanup | null {
  const updates: Partial<LocationMapReferenceUpdates> = {};
  const clearParent = location.parentId === deletedLocationId;

  if (location.mapEntityPoints) {
    const remainingPoints = location.mapEntityPoints.filter((point) => !(
      point.entityRef.type === 'locations'
      && point.entityRef.id === deletedLocationId
    ));
    if (remainingPoints.length !== location.mapEntityPoints.length) {
      updates.mapEntityPoints = remainingPoints;
    }
  }

  if (location.mapEntityAreas) {
    const remainingAreas = location.mapEntityAreas.filter(
      (area) => area.entityRef.id !== deletedLocationId
    );
    if (remainingAreas.length !== location.mapEntityAreas.length) {
      updates.mapEntityAreas = remainingAreas;
    }
  }

  return clearParent || Object.keys(updates).length > 0
    ? { clearParent, updates }
    : null;
}
